import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCcw, Sparkles } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import Newsletter from "../components/site/home/Newsletter";
import MonetizationBlocks from "../components/site/monetization/MonetizationBlocks";
import { useSeo } from "../hooks/useSeo";
import { track } from "../lib/analytics";
import { photoFromPool } from "../data/themePhotos";

// Each option carries weights toward retreat result types.
const QUESTIONS = [
  {
    key: "goal", q: "What's your main goal?",
    options: [
      { label: "Deepen my yoga practice", w: { yoga: 2 } },
      { label: "Rest, recover & de-stress", w: { wellness: 2 } },
      { label: "Adventure on the water", w: { yacht: 2 } },
      { label: "Yoga and the sea combined", w: { sailing: 2 } },
      { label: "Try my very first retreat", w: { beginner: 2 } },
      { label: "Indulge in something special", w: { luxury: 2 } },
    ],
  },
  {
    key: "destination", q: "Where do you dream of going?",
    options: [
      { label: "Greek islands", w: { yacht: 1, sailing: 1 } },
      { label: "Mediterranean coast", w: { wellness: 1, yacht: 1 } },
      { label: "Mountains & lakes", w: { yoga: 1, wellness: 1 } },
      { label: "Anywhere warm & sunny", w: { beginner: 1, yoga: 1 } },
      { label: "Not sure yet", w: { beginner: 1 } },
    ],
  },
  {
    key: "budget", q: "Your budget per person?",
    options: [
      { label: "Under €700", w: { beginner: 2 } },
      { label: "€700–€1,500", w: { yoga: 1, wellness: 1 } },
      { label: "€1,500+", w: { luxury: 2 } },
      { label: "Flexible", w: {} },
    ],
  },
  {
    key: "group", q: "Who's travelling?",
    options: [
      { label: "Just me", w: { yoga: 1, beginner: 1 } },
      { label: "A couple", w: { wellness: 1, luxury: 1 } },
      { label: "A group of friends", w: { yacht: 1, sailing: 1 } },
      { label: "Family", w: { wellness: 1, beginner: 1 } },
    ],
  },
  {
    key: "style", q: "Preferred retreat style?",
    options: [
      { label: "Yoga & movement", w: { yoga: 3 } },
      { label: "Spa & wellness", w: { wellness: 3 } },
      { label: "Sailing & sea", w: { yacht: 3 } },
      { label: "Yoga + sailing combined", w: { sailing: 3 } },
      { label: "Gentle & beginner-friendly", w: { beginner: 3 } },
      { label: "Five-star luxury", w: { luxury: 3 } },
    ],
  },
];

const RESULTS = {
  yoga: { label: "Yoga Retreats", to: "/yoga-retreats", theme: "yoga", desc: "Daily practice, mindful food and time to slow down — your match is a dedicated yoga retreat." },
  wellness: { label: "Wellness Retreats", to: "/wellness-retreats", theme: "spa", desc: "Spas, thermal baths and restorative reset weeks. You're after a true wellness escape." },
  yacht: { label: "Yacht Retreats", to: "/yacht-retreats", theme: "yacht", desc: "Wake up at sea, swim off the deck, sail at sunset. A yacht retreat is calling." },
  sailing: { label: "Yoga Sailing Retreats", to: "/yoga-sailing-retreats", theme: "sunset", desc: "Sun-salutations on deck between secluded coves — the best of yoga and the sea." },
  beginner: { label: "Beginner-Friendly Retreats", to: "/retreats-for-beginners", theme: "meditation", desc: "Gentle, welcoming escapes with no experience needed. Perfect for a first retreat." },
  luxury: { label: "Luxury Retreats", to: "/#escapes", theme: "hotel", desc: "Five-star stays, private guides and quiet indulgence. You deserve the luxury tier." },
};
const ORDER = ["yoga", "wellness", "yacht", "sailing", "beginner", "luxury"];

const computeResult = (answers) => {
  const scores = Object.fromEntries(ORDER.map((k) => [k, 0]));
  Object.values(answers).forEach((opt) => {
    Object.entries(opt.w || {}).forEach(([k, v]) => { scores[k] += v; });
  });
  return ORDER.reduce((best, k) => (scores[k] > scores[best] ? k : best), ORDER[0]);
};

const scrollTo = (id) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function FindMyRetreat() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  useSeo({
    title: "Find My Retreat — Take the Quiz | YogaToYacht",
    description: "Answer five quick questions and we'll match you to the perfect yoga, wellness, yacht or beginner-friendly retreat in Europe.",
    path: "/find-my-retreat",
  });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    track("quiz_started");
  }, []);

  const total = QUESTIONS.length;
  const result = useMemo(() => (done ? computeResult(answers) : null), [done, answers]);

  useEffect(() => {
    if (done && result) track("retreat_result_viewed", { result });
  }, [done, result]);

  const choose = (opt) => {
    const q = QUESTIONS[step];
    const next = { ...answers, [q.key]: opt };
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      track("quiz_completed", { result: computeResult(next), answers: Object.fromEntries(Object.entries(next).map(([k, v]) => [k, v.label])) });
      setDone(true);
    }
  };

  const restart = () => { setAnswers({}); setStep(0); setDone(false); track("quiz_started", { restarted: true }); };

  const r = result ? RESULTS[result] : null;

  return (
    <div data-testid="find-my-retreat" className="bg-brand-bg text-brand-ink">
      <Header onNavigate={() => navigate("/")} />

      {!done ? (
        <section className="mx-auto max-w-[760px] px-6 pt-36 pb-24 md:px-10 md:pt-44">
          <div className="flex items-center justify-between">
            <p className="overline text-brand-inkSoft">Find My Retreat</p>
            <span data-testid="quiz-progress" className="text-sm text-brand-inkSoft">{step + 1} / {total}</span>
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-brand-bg2">
            <div className="h-full rounded-full bg-brand-terracotta transition-all duration-500" style={{ width: `${((step) / total) * 100}%` }} />
          </div>

          <h1 data-testid="quiz-question" className="font-display mt-8 text-3xl leading-tight text-brand-ink md:text-4xl">
            {QUESTIONS[step].q}
          </h1>

          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {QUESTIONS[step].options.map((opt, i) => (
              <button
                key={opt.label}
                data-testid={`quiz-option-${i}`}
                onClick={() => choose(opt)}
                className="group flex items-center justify-between gap-3 rounded-xl border border-brand-line bg-white px-5 py-4 text-left text-sm font-medium text-brand-ink transition-colors hover:border-brand-ink"
              >
                {opt.label}
                <ArrowRight size={16} className="text-brand-terracotta opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            ))}
          </div>

          {step > 0 && (
            <button data-testid="quiz-back" onClick={() => setStep(step - 1)} className="mt-8 inline-flex items-center gap-2 text-sm text-brand-inkSoft hover:text-brand-ink">
              <ArrowLeft size={15} /> Back
            </button>
          )}
        </section>
      ) : (
        <>
          <section data-testid="quiz-result" className="relative overflow-hidden">
            <div className="absolute inset-0">
              <img src={photoFromPool(r.theme, 0)} alt={r.label} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-black/55" />
            </div>
            <div className="relative z-10 mx-auto max-w-[820px] px-6 pt-40 pb-24 text-center md:px-10 md:pt-48">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-medium text-white backdrop-blur"><Sparkles size={14} /> Your match</span>
              <h1 data-testid="quiz-result-title" className="hero-h1 mt-5 text-4xl text-white md:text-6xl">{r.label}</h1>
              <p className="mx-auto mt-5 max-w-xl text-base text-white/85 md:text-lg">{r.desc}</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                {r.to.startsWith("/#") ? (
                  <a href={r.to} data-testid="result-cta-view" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-brand-ink transition-colors hover:bg-brand-bg2">View Matching Retreats <ArrowRight size={15} /></a>
                ) : (
                  <Link to={r.to} data-testid="result-cta-view" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-brand-ink transition-colors hover:bg-brand-bg2">View Matching Retreats <ArrowRight size={15} /></Link>
                )}
                <button data-testid="result-cta-newsletter" onClick={scrollTo("newsletter")} className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-medium text-white backdrop-blur-md transition-colors hover:bg-white/10">Join Newsletter</button>
                <button data-testid="result-cta-recommendation" onClick={scrollTo("monetization-forms")} className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-medium text-white backdrop-blur-md transition-colors hover:bg-white/10">Request a Recommendation</button>
              </div>
              <button data-testid="quiz-restart" onClick={restart} className="mt-7 inline-flex items-center gap-2 text-sm text-white/70 hover:text-white">
                <RotateCcw size={14} /> Retake the quiz
              </button>
            </div>
          </section>

          <MonetizationBlocks browseTo={r.to} recommendationStyle={r.label} />
          <div id="newsletter"><Newsletter /></div>
        </>
      )}

      <Footer />
    </div>
  );
}
